import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import Picture, { SIZES } from '../picture';
import { imageSize, publicImageAtLeast, sizeAsLoaded } from '../../content/imageUrl';
import styles from '../../pages/projectDetail/styles.module.scss';

function ImageCard({ image, onClick, eager, imgRef }) {
  const [loaded, setLoaded] = useState(false);
  // The picture's own shape holds its place; only one of unknown size needs a stand-in height.
  const placeholder = !loaded && !imageSize(image);

  return (
    <div
      className={`${styles.masonryItem} ${placeholder ? styles.cardLoading : ''}`}
      onClick={onClick}
    >
      <div className={styles.overlay} />
      <Picture
        ref={imgRef}
        image={image}
        sizes={SIZES.grid}
        priority={eager}
        fade
        className={`img-fluid ${styles.imageCard}`}
        alt="img"
        onLoad={() => setLoaded(true)}
      />
    </div>
  );
}

// The opened picture. It starts with the copy the grid already has, so it
// appears at once, and replaces it with a detail copy sized for the screen
// as soon as that has arrived. The pictures on either side are then fetched
// ahead.
function LightboxImage({ image, gridCopy, neighbours }) {
  const box = useRef(null);
  const known = imageSize(image);
  // Corrected by the picture itself if it is upright where the recorded size lies on its side.
  const [natural, setNatural] = useState(gridCopy ? [gridCopy.width, gridCopy.height] : null);
  const size = natural ? sizeAsLoaded(known, natural[0], natural[1]) : known;
  // Without a known shape a stand-in could change the layout when replaced.
  const [shown, setShown] = useState(known && gridCopy ? gridCopy.url : '');

  useEffect(() => {
    let current = true;
    const area = box.current.parentElement;
    const density = window.devicePixelRatio || 1;
    const maxHeight = window.innerHeight * 0.85;
    // Real pixels across the picture as it will be drawn.
    const needed = (ref) => {
      const shape = ref === image ? size : imageSize(ref);
      const width = shape ? Math.min(area.clientWidth, maxHeight * (shape[0] / shape[1]), shape[0]) : area.clientWidth;
      return Math.ceil(width * density);
    };
    const url = publicImageAtLeast(image, needed(image));
    const loader = new window.Image();
    const show = () => {
      if (!current) return;
      if (loader.naturalWidth > 0) setNatural([loader.naturalWidth, loader.naturalHeight]);
      setShown(url);
      neighbours.forEach((ref) => { new window.Image().src = publicImageAtLeast(ref, needed(ref)); });
    };
    loader.src = url;
    loader.decode().then(show, show);
    return () => { current = false; };
    // One picture per mount: the parent gives each picture its own key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The same box the full picture has always had: its own size, held within
  // the width of the dialog and 85% of the height of the window.
  const style = size
    ? { width: `min(100%, calc(85vh * ${(size[0] / size[1]).toFixed(5)}), ${size[0]}px)`, height: 'auto', maxHeight: '85vh' }
    : { maxHeight: '85vh', width: 'auto' };

  return (
    <img
      ref={box}
      className="img-fluid m-auto"
      alt="Modal Pic"
      width={size ? size[0] : undefined}
      height={size ? size[1] : undefined}
      style={{ ...style, visibility: shown ? 'visible' : 'hidden' }}
      src={shown || undefined}
    />
  );
}

// The gallery of one project: masonry grid plus lightbox. `images` are image
// references. Used by the public project page and by the admin preview.
export default function ProjectGallery({ images }) {
  const [showModal, setShowModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const gridImages = useRef([]);

  const openModal = (index) => {
    setCurrentIndex(index);
    setShowModal(true);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? images.length - 1 : prev - 1
    );
  };

  const closeModal = () => {
    setShowModal(false);
  };

  // The copy of a picture the grid has already loaded, if it has.
  const gridCopy = (index) => {
    const img = gridImages.current[index];
    return img && img.complete && img.naturalWidth > 0 ? { url: img.currentSrc || img.src, width: img.naturalWidth, height: img.naturalHeight } : null;
  };
  const at = (index) => images[(index + images.length) % images.length];
  const current = Math.min(currentIndex, images.length - 1);

  return (
    <div className={`home-container ${styles.masonryLayout}`}>
      {images.map((image, index) => (
        <ImageCard
          key={index}
          image={image}
          imgRef={(node) => { gridImages.current[index] = node; }}
          onClick={() => openModal(index)}
          eager={index < 2}
        />
      ))}

        <Modal show={showModal} onHide={closeModal} centered size="lg" dialogClassName={styles.transparentModal} style={{ '--bs-modal-box-shadow': 'none' }}>
            <Modal.Header className={styles.transparent} closeButton>
            </Modal.Header>

            <Modal.Body className={`${styles.transparent} col-12 d-flex justify-content-center`}>
                <div className={`${styles.modalBody} ${styles.transparent} col-10 d-flex align-items-center justify-content-center`}>
                    {images.length > 0 && (
                      <LightboxImage
                        key={current}
                        image={images[current]}
                        gridCopy={gridCopy(current)}
                        neighbours={images.length > 1 ? [at(current + 1), at(current - 1)] : []}
                      />
                    )}
                </div>
                  <div
                      className={styles.arrowLeft}
                      onClick={handlePrev}
                  >
                      &#10094; 
                  </div>

                  <div
                      className={styles.arrowRight}
                      onClick={handleNext}
                  >
                      &#10095; 
                  </div>
            </Modal.Body>
        </Modal>
    </div>
  );
}
